import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TabProps, Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../components/Common/UI/Tabs';
import PageSubHeader from '../../../Common/PageSubHeader';
import Icons from '../../../../../components/Common/Icons/Icons';
import { Input } from '../../../../../components/Common/UI/Input';
import { queryFilterParams } from '../../../../../utils';
import JobDetailsTab from './TabsContent/JobDetails';
import { generateSearchResults, useGlobalSearchProvider } from '../../../../context/globalSearch';
import SearchGroups from '../../../Common/GlobalSearch/SearchGroups';
import DeliveryInfo from './TabsContent/DeliveryInfo';
import {
  filterDataColumnsByJobType,
  jobSubTableAccountsData,
  proofingSampleColumns,
  proofingSampleData,
} from './TabsContent/subTableConfig';
import { useJobDetailsProvider } from '../../../../context/jobDetails';
import GeoDeliveryData from './TabsContent/GeoDeliveryData';
import { Button } from '../../../../../components/Common/UI/Button';
import DataTable from '../../../../../components/Common/UI/DataTable';
import { getJobById } from '../data/jobStatus';

function JobDetails() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchTemp, searchApplied, handleOpen, handleContent, handleSearchApplied } = useGlobalSearchProvider();
  const { handleSelectedTable } = useJobDetailsProvider();
  const navigate = useNavigate();
  const job = getJobById(searchParams.get('job'));
  const showDivertReport = (job?.divertedDocuments?.length ?? 0) > 0;

  const tabs: TabProps[] = [
    {
      value: 'job-details',
      label: 'Job Details',
      content: <JobDetailsTab />,
    },
    {
      value: 'delivery-info',
      label: 'Delivery Info',
      content: <DeliveryInfo />,
    },
    {
      value: 'geo-delivery',
      label: 'Geo Delivery Data',
      content: <GeoDeliveryData />,
    },
  ];

  /** When state of temporary search changes - with delay set in content-state results of search. */
  useEffect(() => {
    const getColType = () => {
      if (id === 'Electronic' && searchParams.get('tab') === 'job-details') {
        return 'Electronic';
      }

      return 'Paper';
    };

    const preFilteredData = filterDataColumnsByJobType(
      // id === 'Electronic' ? 'Electronic' : 'Paper',
      getColType(),
      jobSubTableAccountsData
    );

    const setContent = () =>
      handleContent(<SearchGroups searchResults={generateSearchResults(searchTemp, preFilteredData)} />);

    const timeoutId = setTimeout(setContent, 400);

    return () => clearTimeout(timeoutId);
  }, [searchTemp]);

  const handleTabChange = (tab: string) => {
    handleSelectedTable({ row: null, column: null });
    handleSearchApplied({ search: '', field: '' });
    setSearchParams(queryFilterParams({ tab, job: searchParams.get('job') || '' }));
  };

  const handleSwitch = () => {
    const nextId = id === 'Paper' ? 'Electronic' : 'Paper';
    const query = new URLSearchParams(
      queryFilterParams({ tab: searchParams.get('tab') || '', job: searchParams.get('job') || '' })
    ).toString();

    navigate(`/infoTrac/job-status/${nextId}${query ? `?${query}` : ''}`);
    window.location.reload();
  };

  const handleDivertReport = () => {
    const link = document.createElement('a');

    link.href = '/infoTrac/pdfs/divert-report.pdf';
    link.download = 'Divert Report.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <PageSubHeader
        leftContent={
          <div className="flex items-center gap-2">
            <Icons.JobStatus className="size-6" />
            <span>Job Status</span>
          </div>
        }
        breadcrumbs={[
          { path: '/infoTrac/', label: <Icons.HomeOutlined />, disableBg: true },
          { path: '/infoTrac', label: 'InfoTRAC' },
          { path: '/infoTrac/job-status', label: 'Job Status' },
          { path: `/infoTrac/job-status/${id}`, label: id || 'Electronic' },
        ]}
        rightContent={
          <div className="flex items-center gap-2">
            <span className="transition-colors text-primary-70 hover:text-primary rounded-lg px-1.5">Switch to:</span>
            <Button className="py-1 px-4" onClick={handleSwitch}>
              {id === 'Paper' ? 'Electronic 200680' : 'Paper 200681'}
            </Button>
          </div>
        }
      />

      <Tabs defaultValue={searchParams.get('tab') || 'job-details'} onValueChange={handleTabChange}>
        <div className="page-heading px-4 py-2 flex items-center justify-between bg-slate-100">
          <TabsList>
            {tabs.map(({ value, label }) => (
              <TabsTrigger key={`${value}-trigger`} value={value}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex items-center gap-4">
            {showDivertReport ? (
              <Button variant="secondary" onClick={handleDivertReport} className="flex items-center gap-3 px-5">
                <Icons.PDF />
                Divert Report
              </Button>
            ) : null}
            <Input
              placeholder="Search Account"
              icon={<Icons.Search />}
              variant="white"
              inputClassName="min-w-60 pr-9"
              value={searchApplied.search}
              readOnly
              onClick={() => handleOpen(true)}
            />
          </div>
        </div>

        {tabs.map(({ value, content }) => (
          <TabsContent key={value} value={value} className="mt-0">
            {content}
          </TabsContent>
        ))}
      </Tabs>
      <div className="flex justify-center items-center mb-4 mt-4">
        <div className="max-w-[80%] w-full">
          <h3 className="mb-2 text-status-draft">Proofing Sample</h3>
          <DataTable variant="bordered" data={proofingSampleData} columns={proofingSampleColumns} />
        </div>
      </div>
    </div>
  );
}

export default JobDetails;
